/**
 * Durable script job queue (admin.script_jobs + server_python worker).
 */
import { apiEndpoints } from '@config/env';
import { getData, postData } from '@utils/global';
import { debug, warn } from '@utils/console';
import { processStore } from '@store/processStore';
import { sseManager } from '@store/sseManager';
import { authManager } from '@utils/authManager';

export type ScriptJobStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed_retryable'
  | 'failed_permanent'
  | 'canceled'
  | 'timed_out';

export interface ScriptJob {
  id: string;
  status: ScriptJobStatus;
  class_name: string;
  script_name: string;
  parameters: Record<string, unknown>;
  requested_by: string;
  attempt: number;
  max_attempts: number;
  failure_mode?: string | null;
  process_id?: string | null;
  return_code?: number | null;
  script_succeeded?: boolean | null;
  error_message?: string | null;
  output_summary?: string | null;
  context?: Record<string, unknown> | null;
  cancel_requested?: boolean;
  created_at?: string;
  started_at?: string | null;
  finished_at?: string | null;
}

export interface ScriptExecutePayload {
  class_name: string;
  script_name: string;
  parameters: Record<string, unknown>;
  context?: Record<string, unknown>;
  priority?: number;
  /** Legacy execute_script payloads may include project_id at top level */
  project_id?: string | number;
}

export interface LegacyExecuteScriptResponse {
  success?: boolean;
  message?: string;
  status?: number;
  type?: string;
  data?: {
    process_id?: string;
    job_id?: string;
    return_code?: number;
    script_succeeded?: boolean;
    results?: unknown;
    output_lines?: string[];
    error_lines?: string[];
    warning_lines?: string[];
    timeout?: boolean;
    process_already_running?: boolean;
    running_processes?: unknown[];
  };
  process_id?: string;
}

const TERMINAL: ScriptJobStatus[] = [
  'succeeded',
  'failed_retryable',
  'failed_permanent',
  'canceled',
  'timed_out',
];

const JOB_POLL_MS = 2000;
const JOB_MAX_WAIT_MS = 65 * 60 * 1000;
/** Consecutive 404s before we declare the row gone (tolerates a transient read failure). */
const MAX_MISSING_POLLS = 3;

/**
 * Priority for jobs a user is sitting in front of, waiting on (e.g. the race-course upload's
 * 1_parseXml.py run).
 *
 * MUST stay above server_stream's LIVE_MANEUVER_JOB_PRIORITY (20). The python worker runs exactly one
 * job at a time and claims `ORDER BY priority DESC, created_at ASC`, so at the default priority 0 an
 * interactive job is starved for as long as live processing keeps enqueueing maneuver/phase jobs —
 * i.e. for the whole streaming session. Do NOT use this for bulk/backfill enqueues: those would then
 * outrank live processing and stall the live reports.
 */
export const INTERACTIVE_JOB_PRIORITY = 30;

function isTerminal(status: string): boolean {
  return TERMINAL.includes(status as ScriptJobStatus);
}

/**
 * Metadata stored in admin.script_jobs.context — worker runs `parameters` + class/script columns.
 * `replay` mirrors the execution payload so retry/list never depends on UI project selection.
 */
export function buildScriptJobContext(
  class_name: string,
  script_name: string,
  parameters: Record<string, unknown>,
  extra?: Record<string, unknown>
): Record<string, unknown> {
  const ctx: Record<string, unknown> = { ...(extra ?? {}) };
  ctx.class_name = class_name;
  ctx.script_name = script_name;
  for (const key of ['project_id', 'dataset_id', 'date', 'source_name', 'timezone'] as const) {
    const v = parameters[key];
    if (v !== undefined && v !== null && ctx[key] === undefined) {
      ctx[key] = v;
    }
  }
  ctx.replay = {
    class_name,
    script_name,
    parameters: { ...parameters },
  };
  return ctx;
}

/** Connect SSE (port 8049) before enqueue so progress works in modals and admin UI. */
export async function ensureScriptExecutionSse(): Promise<void> {
  try {
    await sseManager.connectToServer(8049);
  } catch (e) {
    warn('[scriptJobs] SSE connect failed:', e);
  }
}

export interface RunScriptJobHandle {
  job_id: string;
  process_id: string;
  completion: Promise<LegacyExecuteScriptResponse>;
}

/**
 * Enqueue immediately (returns process_id for UI), then wait in `completion`.
 * Use this when Showing WaitingModal or other live progress — not blocking `executeScriptJob`.
 */
export async function runScriptJobForUi(
  payload: ScriptExecutePayload,
  options?: { signal?: AbortSignal; showToast?: boolean }
): Promise<RunScriptJobHandle> {
  await ensureScriptExecutionSse();
  const { job_id, process_id } = await submitScriptJob(payload, {
    showToast: options?.showToast,
  });
  debug('[scriptJobs] runScriptJobForUi enqueued', { job_id, process_id, script: payload.script_name });
  const completion = waitForScriptJob(job_id, process_id, options?.signal);
  return { job_id, process_id, completion };
}

export async function submitScriptJob(
  payload: ScriptExecutePayload,
  options?: {
    showToast?: boolean;
    connectSse?: boolean;
    /** When false, do not register queued jobs as active processes (admin bulk enqueue). */
    trackProcess?: boolean;
  }
): Promise<{ job_id: string; process_id: string; status: string }> {
  if (options?.connectSse === true) {
    await ensureScriptExecutionSse();
  }
  const parameters = { ...payload.parameters };
  const context = buildScriptJobContext(
    payload.class_name,
    payload.script_name,
    parameters,
    {
      ...(payload.context ?? {}),
      ...(payload.project_id != null ? { project_id: payload.project_id } : {}),
      // A job at interactive priority is one a user enqueued by hand and is waiting on, so mark it as
      // such: the admin clear (POST /api/script-jobs/clear) spares interactive rows that have not run
      // yet. Without it, arming live processing mid-upload deleted the queued race-course parse.
      ...(payload.priority != null && payload.priority >= INTERACTIVE_JOB_PRIORITY
        ? { interactive: true }
        : {}),
    }
  );
  const body: Record<string, unknown> = {
    class_name: payload.class_name,
    script_name: payload.script_name,
    parameters,
    context,
    priority: payload.priority,
  };
  const res = await postData(apiEndpoints.admin.script_jobs, body);
  if (!res?.success || !res?.data?.job_id) {
    throw new Error(res?.message || 'Failed to enqueue script job');
  }
  const processId = res.data.process_id || res.data.job_id;
  const trackProcess = options?.trackProcess !== false;
  if (trackProcess) {
    processStore.startProcess(processId, 'script_execution', options?.showToast);
    sseManager.onProcessStart(processId, 'script_execution');
  }
  return {
    job_id: res.data.job_id,
    process_id: processId,
    status: res.data.status || 'queued',
  };
}

/**
 * `missing` is true only for a 404 — the row is gone, not merely unreadable. Pollers must be able to
 * tell those apart: server_stream deletes every queued job when live processing is armed
 * (clearScriptJobQueue with include_queued), and polling a deleted row looks identical to "still
 * queued" otherwise.
 */
export async function fetchScriptJob(jobId: string): Promise<{ job: ScriptJob | null; missing: boolean }> {
  const response = await authManager.makeAuthenticatedRequest(apiEndpoints.admin.script_job(jobId), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (response.status === 404) {
    debug('[scriptJobs] Job not found while polling:', jobId);
    return { job: null, missing: true };
  }
  if (!response.ok) {
    warn(`[scriptJobs] Failed to load job ${jobId}: ${response.status} ${response.statusText}`);
    return { job: null, missing: false };
  }
  const res = await response.json();
  if (!res?.success) return { job: null, missing: false };
  return { job: res.data?.job ?? null, missing: false };
}

export async function getScriptJob(jobId: string): Promise<ScriptJob | null> {
  const { job } = await fetchScriptJob(jobId);
  return job;
}

export async function cancelScriptJob(jobId: string): Promise<{ status: string; message?: string }> {
  const res = await postData(apiEndpoints.admin.script_job_cancel(jobId), {});
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to cancel script job');
  }
  const job = res.data?.job as ScriptJob | undefined;
  return {
    status: job?.status || 'unknown',
    message: res.message,
  };
}

/** Mark DB rows stuck in running (e.g. after server restart) as failed_retryable. */
export async function reconcileStaleScriptJobs(): Promise<string[]> {
  const res = await postData(apiEndpoints.admin.script_jobs_reconcile_stale, {});
  if (res?.status === 403) {
    debug('[scriptJobs] reconcile-stale skipped (insufficient permissions)');
    return [];
  }
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to reconcile stale script jobs');
  }
  return res.data?.job_ids ?? [];
}

export async function retryScriptJob(jobId: string, resetAttempt = false): Promise<void> {
  await postData(apiEndpoints.admin.script_job_retry(jobId), { reset_attempt: resetAttempt });
}

function jobToLegacyResponse(job: ScriptJob): LegacyExecuteScriptResponse {
  const processId = job.process_id || job.id;
  const outputLines = job.output_summary ? job.output_summary.split('\n') : [];
  const errorLines = job.error_message ? [job.error_message] : [];
  const base = {
    process_id: processId,
    job_id: job.id,
    return_code: job.return_code ?? (job.status === 'succeeded' ? 0 : -1),
    script_succeeded: job.script_succeeded ?? job.status === 'succeeded',
    output_lines: outputLines,
    error_lines: errorLines,
    warning_lines: [] as string[],
    timeout: job.status === 'timed_out',
  };

  if (job.status === 'succeeded') {
    return {
      success: true,
      message: 'Script executed successfully',
      data: { ...base, results: { output: job.output_summary || 'OK' } },
    };
  }
  if (job.status === 'timed_out') {
    return {
      success: false,
      message: 'Script execution timed out',
      data: { ...base, timeout: true },
      status: 408,
    };
  }
  return {
    success: false,
    message: job.error_message || 'Script execution failed',
    data: base,
    status: 500,
  };
}

/**
 * Poll job + SSE until terminal; returns legacy execute_script-shaped response.
 */
export async function waitForScriptJob(
  jobId: string,
  processId: string,
  signal?: AbortSignal
): Promise<LegacyExecuteScriptResponse> {
  const start = Date.now();

  return new Promise((resolve, reject) => {
    let settled = false;
    let missingPolls = 0;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let unsubscribe: (() => void) | undefined;

    const finish = (result: LegacyExecuteScriptResponse) => {
      if (settled) return;
      settled = true;
      if (pollTimer) clearInterval(pollTimer);
      if (unsubscribe) unsubscribe();
      const status =
        result.data?.timeout ? 'timeout'
        : result.success ? 'complete'
        : 'error';
      processStore.completeProcess(processId, status, result.data);
      resolve(result);
    };

    unsubscribe = sseManager.subscribe((msg) => {
      if (msg.process_id !== processId) return;
      if (
        msg.event === 'process_complete' ||
        msg.event === 'process_timeout' ||
        msg.event === 'process_cancelled'
      ) {
        void (async () => {
          const job = await getScriptJob(jobId);
          if (job && isTerminal(job.status)) {
            finish(jobToLegacyResponse(job));
          }
        })();
      }
    });

    const poll = async () => {
      if (signal?.aborted) {
        reject(new Error('Request cancelled'));
        return;
      }
      if (Date.now() - start > JOB_MAX_WAIT_MS) {
        reject(new Error('Timed out waiting for script job'));
        return;
      }
      try {
        const { job, missing } = await fetchScriptJob(jobId);
        if (missing) {
          missingPolls += 1;
          if (missingPolls >= MAX_MISSING_POLLS) {
            // The row was deleted while we waited — server_stream clears the queue when live
            // processing is armed. Report it now instead of polling a 404 until the caller's timeout,
            // which surfaced as an unexplained "still working" hang.
            finish({
              success: false,
              message: 'Script job was removed from the queue before it ran',
              status: 404,
              data: {
                process_id: processId,
                job_id: jobId,
                return_code: -1,
                script_succeeded: false,
                output_lines: [],
                error_lines: ['Job no longer exists (queue was cleared)'],
                warning_lines: [],
                timeout: false,
              },
            });
          }
          return;
        }
        missingPolls = 0;
        if (job && isTerminal(job.status)) {
          finish(jobToLegacyResponse(job));
        }
      } catch (e) {
        warn('[scriptJobs] poll error:', e);
      }
    };

    pollTimer = setInterval(poll, JOB_POLL_MS);
    void poll();
  });
}

/**
 * Enqueue and wait — drop-in replacement for postData(execute_script, payload).
 */
export async function executeScriptJob(
  payload: ScriptExecutePayload,
  signal?: AbortSignal
): Promise<LegacyExecuteScriptResponse> {
  if (signal?.aborted) {
    return { success: false, message: 'Request cancelled', type: 'AbortError' };
  }
  const { completion } = await runScriptJobForUi(payload, { signal });
  return completion;
}

export interface ScriptQueueState {
  paused: boolean;
  paused_at?: string | null;
  paused_by?: string | null;
  updated_at?: string | null;
}

export async function getScriptQueueState(): Promise<ScriptQueueState> {
  const res = await getData(apiEndpoints.admin.script_jobs_queue_state);
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to load script queue state');
  }
  return {
    paused: res.data?.paused === true,
    paused_at: res.data?.paused_at ?? null,
    paused_by: res.data?.paused_by ?? null,
    updated_at: res.data?.updated_at ?? null,
  };
}

export async function pauseScriptQueue(): Promise<ScriptQueueState> {
  const res = await postData(apiEndpoints.admin.script_jobs_pause, {});
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to pause script queue');
  }
  return {
    paused: res.data?.paused === true,
    paused_at: res.data?.paused_at ?? null,
    paused_by: res.data?.paused_by ?? null,
  };
}

export async function resumeScriptQueue(): Promise<ScriptQueueState> {
  const res = await postData(apiEndpoints.admin.script_jobs_resume, {});
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to resume script queue');
  }
  return {
    paused: res.data?.paused === true,
    paused_at: res.data?.paused_at ?? null,
    paused_by: res.data?.paused_by ?? null,
  };
}

export interface CancelAllScriptJobsResult {
  canceled_count: number;
  running_count: number;
  skipped_count: number;
  matched_count: number;
}

/**
 * Cancel every job still waiting to run (and, with includeRunning, the one in flight).
 *
 * Distinct from clearScriptJobs({ includeQueued: true }), which deletes queued rows: a deleted row leaves
 * waitForScriptJob reporting "job was removed from the queue before it ran", where a canceled one reaches
 * a clean terminal status. Cancel first, then clear, so the import guard on the server also lets go.
 */
export async function cancelAllScriptJobs(options?: {
  includeRunning?: boolean;
  scriptNames?: string[];
  projectId?: string | number;
}): Promise<CancelAllScriptJobsResult> {
  const body: Record<string, unknown> = {
    include_running: options?.includeRunning === true,
  };
  if (options?.scriptNames?.length) {
    body.script_names = options.scriptNames;
  }
  if (options?.projectId != null && String(options.projectId).trim() !== '') {
    body.project_id = String(options.projectId);
  }
  const res = await postData(apiEndpoints.admin.script_jobs_cancel_all, body);
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to cancel script jobs');
  }
  return {
    canceled_count: res.data?.canceled_count ?? 0,
    running_count: res.data?.running_count ?? 0,
    skipped_count: res.data?.skipped_count ?? 0,
    matched_count: res.data?.matched_count ?? 0,
  };
}

export async function clearScriptJobs(options?: {
  includeQueued?: boolean;
  scriptNames?: string[];
  projectId?: string | number;
}): Promise<number> {
  const body: Record<string, unknown> = {
    include_queued: options?.includeQueued === true,
  };
  if (options?.scriptNames?.length) {
    body.script_names = options.scriptNames;
  }
  if (options?.projectId != null && String(options.projectId).trim() !== '') {
    body.project_id = String(options.projectId);
  }
  const res = await postData(apiEndpoints.admin.script_jobs_clear, body);
  if (!res?.success) {
    throw new Error(res?.message || 'Failed to clear script jobs');
  }
  return res.data?.deleted_count ?? 0;
}

export async function listScriptJobs(params?: {
  status?: string;
  class_name?: string;
  script_name?: string;
  project_id?: string | number;
  limit?: number;
}): Promise<ScriptJob[]> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.class_name) qs.set('class_name', params.class_name);
  if (params?.script_name) qs.set('script_name', params.script_name);
  if (params?.project_id != null) qs.set('project_id', String(params.project_id));
  if (params?.limit) qs.set('limit', String(params.limit));
  const url = qs.toString()
    ? `${apiEndpoints.admin.script_jobs}?${qs}`
    : apiEndpoints.admin.script_jobs;
  const res = await getData(url);
  return res?.data?.jobs ?? [];
}
